import { supabase } from "@/lib/supabase";
import type { Database } from "@/integrations/supabase/types";

export type CleaningTaskRow = Database["public"]["Tables"]["cleaning_tasks"]["Row"];
export type TaskExecutionLogRow = Database["public"]["Tables"]["task_execution_logs"]["Row"];

export type WorkSessionTask = Pick<
  CleaningTaskRow,
  | "id"
  | "status"
  | "location_id"
  | "assigned_staff_id"
  | "org_id"
  | "scheduled_at"
  | "completed_at"
  | "started_at"
  | "task_type"
  | "check_in_metadata"
>;

export function activeSessionStorageKey(propertyId: string): string {
  return `active_session_${propertyId}`;
}

export function hasActiveWorkSession(propertyId: string): boolean {
  return typeof window !== "undefined" && localStorage.getItem(activeSessionStorageKey(propertyId)) === "true";
}

export function setActiveWorkSession(propertyId: string, active: boolean): void {
  if (typeof window === "undefined") return;
  if (active) {
    localStorage.setItem(activeSessionStorageKey(propertyId), "true");
    return;
  }
  localStorage.removeItem(activeSessionStorageKey(propertyId));
}

export function countRemainingPropertyTasks(
  sections: Array<{ tasksTotal?: number; tasksCompleted?: number }>
): number {
  return sections.reduce((sum, section) => {
    const total = section.tasksTotal ?? 0;
    const completed = section.tasksCompleted ?? 0;
    return sum + Math.max(0, total - completed);
  }, 0);
}

export function pickTaskForWorkSession<
  T extends { status: string | null; task_type?: string | null },
>(tasks: T[]): T | null {
  if (tasks.length === 0) return null;
  const open = tasks.filter((task) => task.status === "pending" || task.status === "in_progress");
  const sessionTask = open.find(
    (task) => task.task_type !== "employee_extra" && task.task_type !== "coordinator_single",
  );
  return sessionTask ?? open[0] ?? tasks[0] ?? null;
}

export function shouldWriteCheckInMetadata(status: string | null | undefined): boolean {
  return status === "pending" || status === "in_progress";
}

export function formatCheckOutNotes(params: {
  latitude: number | null;
  longitude: number | null;
  remainingTasks: number;
}): string {
  const gps = `GPS: ${params.latitude}, ${params.longitude}`;
  if (params.remainingTasks > 0) {
    return `${gps}; partial_checkout; remaining_tasks=${params.remainingTasks}`;
  }
  return gps;
}

async function fetchLocationSessionTasks(locationId: string, orgId: string): Promise<WorkSessionTask[]> {
  const { data, error } = await supabase
    .from("cleaning_tasks")
    .select(
      "id, status, location_id, assigned_staff_id, org_id, scheduled_at, completed_at, started_at, task_type, check_in_metadata",
    )
    .eq("location_id", locationId)
    .eq("org_id", orgId)
    .neq("status", "cancelled")
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) throw error;
  return data ?? [];
}

export async function findLocationTaskForWorkSession(
  locationId: string,
  orgId: string
): Promise<WorkSessionTask | null> {
  const tasks = await fetchLocationSessionTasks(locationId, orgId);
  return pickTaskForWorkSession(tasks);
}

export async function findCheckoutTaskForLocation(params: {
  locationId: string;
  orgId: string;
  userId: string;
}): Promise<WorkSessionTask | null> {
  const tasks = await fetchLocationSessionTasks(params.locationId, params.orgId);
  if (tasks.length === 0) return null;

  const taskIds = tasks.map((task) => task.id);
  const { data: lastCheckIn, error: logError } = await supabase
    .from("task_execution_logs")
    .select("task_id")
    .eq("user_id", params.userId)
    .eq("action_type", "CHECK_IN")
    .in("task_id", taskIds)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (logError) {
    console.error("[cleanerWorkSession] last CHECK_IN lookup failed", logError);
  }

  if (lastCheckIn?.task_id) {
    const matched = tasks.find((task) => task.id === lastCheckIn.task_id);
    if (matched) return matched;
  }

  return pickTaskForWorkSession(tasks);
}
